import { getRoute, getRouteV2 } from 'Config';

import type { HandlerConfig } from './types';

// Tinder Web supported deep links (from native intent)
// Potentially move to custom handler https://developer.mozilla.org/en-US/docs/Web/API/Navigator/registerProtocolHandler/Web-based_protocol_handlers#background
// https://caniuse.com/registerprotocolhandler

// https://confluence.gotinder.biz/pages/viewpage.action?pageId=14619128
// https://confluence.gotinder.biz/pages/viewpage.action?pageId=81175828
export const INTENT_PROTOCOL = 'tinder:';

export const INTENT_ADD_PHOTOS = 'addPhotos';
export const INTENT_ADD_INTEREST = 'addInterests';
export const INTENT_ANTHEM = 'anthem';
export const INTENT_CAMPAIGN = 'events';
export const INTENT_CURATED_CARD_STACK = 'curatedCardStack';
export const INTENT_DISCOVERY = 'discovery';
export const INTENT_EDIT_PROFILE = 'editProfile';
export const INTENT_EXPLORE = 'explore';
export const INTENT_EXPLORE_CATALOG = 'catalogPage';
export const INTENT_EXPLORE_SELFIE = 'explore/selfie';
export const INTENT_MESSAGES_FEED = 'messagesFeed';
export const INTENT_RECOMMENDED_CARD_STACK = 'recommendedCardstack';
export const INTENT_RELATIONSHIP_INTENT = 'relationshipIntent';
export const INTENT_REMOTE_VIEW = 'tinderAuth/web';
export const INTENT_SCHOOL = 'school';
export const INTENT_SETTINGS = 'settings';
export const INTENT_SPOTIFY = 'spotify';
export const INTENT_WEB_PROFILE = 'u';
export const INTENT_WORK = 'work';

// Trust
export const INTENT_AGE_VERIFICATION = 'age-verification';

// Revenue
export const INTENT_FAST_MATCH = 'fast-match';
export const INTENT_GET_GOLD = 'getGold';
export const INTENT_GOLD_HOME = 'goldHome';
export const INTENT_MY_TINDER_PLUS = 'myTinderPlus';
export const INTENT_PASSPORT = 'passport';
export const INTENT_PURCHASE_FLOW = 'purchaseFlow';
export const INTENT_START_BOOST = 'startBoost';
export const INTENT_TYPE_LOCATION = 'location';

// Core
export const INTENT_MATCHMAKER = 'matchmaker';

// URL parse will change to lowercase as url is case-insensitive
type ValidHandlerName = keyof typeof rawConfig;

const transformCase = (obj: Record<ValidHandlerName, HandlerConfig>) =>
  Object.fromEntries(Object.entries(obj).map(([k, v]) => [k.toLowerCase(), v]));

// Route mapping
// 1. 'route': where the intent will be redirected to
// 2. 'handler': the handler function to handle the intent (optional)
// 3. 'userActionOnly': only handle the intent from user action not query param (optional)

const rawConfig = {
  [INTENT_ANTHEM]: {
    route: getRoute('editProfileSelectAnthem'),
  },
  [INTENT_DISCOVERY]: {
    route: getRoute('recs'),
  },
  [INTENT_RECOMMENDED_CARD_STACK]: {
    route: getRoute('recs'),
  },
  [INTENT_EDIT_PROFILE]: {
    route: getRoute('editProfile'),
  },
  [INTENT_SCHOOL]: {
    route: getRoute('editProfileSelectSchool'),
  },
  [INTENT_SETTINGS]: {
    route: getRoute('settings'),
  },
  [INTENT_WORK]: {
    route: getRoute('editProfile'),
  },
  [INTENT_ADD_INTEREST]: {
    route: getRoute('editProfileInterests'),
  },
  [INTENT_MESSAGES_FEED]: {
    route: getRoute('matches'),
  },
  [INTENT_CAMPAIGN]: {
    handler: () =>
      import('Store/intent/handlers/campaign' /* webpackChunkName: "intent-handler-campaign" */),
    userActionOnly: true,
  },
  [INTENT_CURATED_CARD_STACK]: {
    handler: () =>
      import(
        'Store/intent/handlers/curatedCardStack' /* webpackChunkName: "intent-handler-curatedCardStack" */
      ),
    userActionOnly: true,
  },
  [INTENT_REMOTE_VIEW]: {
    handler: () =>
      import(
        'Store/intent/handlers/remoteView' /* webpackChunkName: "intent-handler-remoteView" */
      ),
  },
  [INTENT_SPOTIFY]: {
    route: getRoute('editProfileSelectTopArtists'),
  },
  [INTENT_EXPLORE]: {
    handler: () =>
      import('Store/intent/handlers/explore' /* webpackChunkName: "intent-handler-explore" */),
  },
  [INTENT_EXPLORE_CATALOG]: {
    route: getRoute('explore'),
  },
  [INTENT_EXPLORE_SELFIE]: {
    // just redirect to profile for now, we could trigger selfie verification if we want
    // a bit complicated as all selfie entry point is using React hook (not in Saga)
    route: getRoute('profile'),
  },
  // revenue
  [INTENT_FAST_MATCH]: {
    route: getRoute('likesYou'),
  },
  [INTENT_GOLD_HOME]: {
    route: getRoute('goldHome'),
    handler: () =>
      import('Store/intent/handlers/goldHome' /* webpackChunkName: "intent-handler-goldHome" */),
  },
  [INTENT_MY_TINDER_PLUS]: {
    route: getRoute('subscriptionSettings'),
  },
  [INTENT_PASSPORT]: {
    route: getRoute('passport'),
    handler: () =>
      import('Store/intent/handlers/passport' /* webpackChunkName: "intent-handler-passport" */),
  },
  [INTENT_RELATIONSHIP_INTENT]: {
    handler: () =>
      import(
        'Store/intent/handlers/relationshipIntent' /* webpackChunkName: "intent-handler-relationshipIntent" */
      ),
  },
  [INTENT_TYPE_LOCATION]: {
    route: getRoute('passport'),
  },
  [INTENT_GET_GOLD]: {
    handler: () =>
      import('Store/intent/handlers/getGold' /* webpackChunkName: "intent-handler-getGold" */),
  },
  [INTENT_START_BOOST]: {
    handler: () =>
      import(
        'Store/intent/handlers/startBoost' /* webpackChunkName: "intent-handler-startBoost" */
      ),
    userActionOnly: true,
  },
  [INTENT_PURCHASE_FLOW]: {
    handler: () =>
      import(
        'Store/intent/handlers/purchaseFlow' /* webpackChunkName: "intent-handler-purchaseFlow" */
      ),
  },

  // core
  [INTENT_MATCHMAKER]: {
    handler: () =>
      import(
        'Store/intent/handlers/matchmaker' /* webpackChunkName: "intent-handler-matchmaker" */
      ),
  },
  [INTENT_ADD_PHOTOS]: {
    route: getRouteV2('editProfile'),
  },
};
export const HANDLER_MAPPING = transformCase(rawConfig);

export const UrlFields = ['host', 'pathname', 'protocol', 'query', 'search'] as const;
